import * as React from "react";
import { useHistory } from "react-router-dom";
import { logger } from "@origin-digital/reporting-client";
import { search, SearchResult, trackSearch } from "../../services/search";
import { useAppContext } from "../../AppContext";
import { routes } from "../../helpers/router.helper";
import { findArticleLabel } from "../../services/label";
import { SearchBar } from "./SearchBar";
import { useDebounce } from "./useDebounce";
import { isOptionSelectEvent, findOption } from "./helper";

export const SearchBarController = (): JSX.Element => {
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState<boolean>(false);
  const [options, setOptions] = React.useState<SearchResult[]>([]);

  const debouncedQuery = useDebounce(query);
  const {
    config,
    session,
    navigation,
    articleLabels,
    fetchSessionId,
    visitorToken = "",
  } = useAppContext();

  const history = useHistory();

  const fetchSearchResults = (): void => {
    setLoading(true);
    search(config.bold360host, query, session.data?.sessionId).then(
      (results) => {
        setLoading(false);
        setOptions(results);
      }
    );
  };

  React.useEffect((): void => {
    if (query.trim().length > 2) {
      if (session.data?.sessionId) {
        fetchSearchResults();
      } else {
        logger.debug("sessionId expired!, fetching new sessionId");
        fetchSessionId();
      }
    } else {
      setOptions([]);
    }
  }, [debouncedQuery, session]);

  const onTrackSearch = (searchString: string) => {
    if (searchString.length > 0) {
      trackSearch(
        config.bold360host,
        searchString,
        visitorToken,
        session.data?.sessionId
      );
    }
  };

  const onChange = (event, value, reason): void => {
    setOpen(false);
    if (isOptionSelectEvent(reason)) {
      const selectedArticle = findOption(options, value);
      if (selectedArticle) {
        if (event) {
          // when an option is selected from autocomplete we seem to get
          // two onChange(reset) events. This prevents us sending two
          // tracking events
          onTrackSearch(query);
          setQuery("");
          const { category, subcategory } = findArticleLabel(
            selectedArticle.id,
            articleLabels,
            navigation
          );
          history.push(
            routes.article(category, subcategory, selectedArticle.id, value)
          );
        }
      }
    } else {
      setQuery(value);
    }
  };

  const onBlur = (): void => {
    setOptions([]);
    setOpen(false);
  };

  const onEnter = (): void => {
    if (!options.length) {
      onTrackSearch(query);
    }
    setOpen(true);
  };

  const shouldOpen = (options.length > 0 && query.length > 2) || open;

  return (
    <SearchBar
      loading={loading}
      open={shouldOpen}
      onBlur={onBlur}
      onChange={onChange}
      results={options}
      query={query}
      onEnter={onEnter}
    />
  );
};
